import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";

import {
  CheckCircle,
  ExclamationCircle,
  InformationCircle,
} from "../../icons/svg/outline";

const TYPE_ICON = {
  success: CheckCircle,
  info: InformationCircle,
  error: ExclamationCircle,
};

const TYPE_ICON_CLASS = {
  success: "text-emerald-600",
  info: "text-sky-600",
  error: "text-red-600",
};

const EMPHASIS_CLASS = "font-semibold text-emerald-600";

/**
 * Splits on paired `**...**` segments; inner text is rendered bold + green. Unpaired
 * `**` or text outside pairs stays normal body styling.
 *
 * @param {string} message
 * @returns {Array<{ kind: 'plain'|'emphasis', text: string }>}
 */
function parseDoubleStarMessage(message) {
  if (typeof message !== "string" || !message.includes("**")) {
    return [{ kind: "plain", text: message ?? "" }];
  }
  const segments = [];
  const re = /\*\*([\s\S]*?)\*\*/g;
  let last = 0;
  let m = re.exec(message);
  while (m !== null) {
    if (m.index > last) {
      segments.push({ kind: "plain", text: message.slice(last, m.index) });
    }
    segments.push({ kind: "emphasis", text: m[1] });
    last = m.index + m[0].length;
    m = re.exec(message);
  }
  if (last < message.length) {
    segments.push({ kind: "plain", text: message.slice(last) });
  }
  return segments.length > 0 ? segments : [{ kind: "plain", text: message }];
}

/**
 * @param {{ message: string }} props
 */
function NotificationMessageBody({ message }) {
  const segments = parseDoubleStarMessage(message);
  return (
    <>
      {segments.map((seg, i) =>
        seg.kind === "emphasis" ? (
          <strong key={i} className={EMPHASIS_CLASS}>
            {seg.text}
          </strong>
        ) : (
          <span key={i}>{seg.text}</span>
        )
      )}
    </>
  );
}

/**
 * Bottom-right stacking notifications (newest toward bottom). Renders via portal to
 * `document.body` so layout/overflow on the page does not clip toasts.
 *
 * @param {Object} props
 * @param {Array<{ id: string, message: string, type?: 'success'|'info'|'error' }>} props.items — `message` may use `**highlight**`; highlighted spans render bold + green
 * @param {(id: string) => void} props.onDismiss
 */
export default function NotificationStack({ items, onDismiss }) {
  if (typeof document === "undefined" || items.length === 0) {
    return null;
  }

  return createPortal(
    <div
      className="pointer-events-none caret-none fixed font-inter bottom-4 right-4 z-[10050] flex max-w-[min(100vw-2rem,20rem)] flex-col-reverse gap-2"
      aria-live="polite"
      aria-relevant="additions"
    >
      {items.map(({ id, message, type: rawType }) => {
        const type =
          rawType === "success" || rawType === "error" ? rawType : "info";
        const Icon = TYPE_ICON[type] ?? InformationCircle;
        const iconClass = TYPE_ICON_CLASS[type] ?? TYPE_ICON_CLASS.info;
        return (
          <div
            key={id}
            className="pointer-events-auto relative rounded-lg border border-gray-200 bg-white p-3 pr-9 text-sm text-gray-900 shadow-lg"
            role={type === "error" ? "alert" : "status"}
          >
            <button
              type="button"
              className="absolute right-1.5 top-1.5 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label="Dismiss"
              onClick={() => onDismiss(id)}
            >
              <IoClose className="h-5 w-5" />
            </button>
            <div className="flex gap-2 pr-1">
              <Icon
                className={`h-5 w-5 shrink-0 ${iconClass}`}
                aria-hidden
              />
              <p className="min-w-0 flex-1 leading-snug">
                <NotificationMessageBody message={message} />
              </p>
            </div>
          </div>
        );
      })}
    </div>,
    document.body
  );
}
